import { useEffect, useRef, useCallback } from "react";
import { reportViolation, uploadSnapshot } from "../api/client";

/**
 * Proctor — strict monitoring component.
 *
 * 1. Webcam snapshots every 2s (primary evidence for admin)
 * 2. Face detection with landmarks (gaze, head turn, position)
 * 3. Tab switch / blur detection
 */

const SNAPSHOT_INTERVAL = 10_000; // every 10 seconds (reduced image volume)
const FACE_CHECK_INTERVAL = 500;  // check face every 0.5s for fast detection
const VIOLATION_COOLDOWN = 8_000;
const NO_FACE_COOLDOWN = 3_000;   // faster re-warn for no_face (3s vs 8s)
const CONSECUTIVE_NO_FACE_THRESHOLD = 2; // 2 misses × 0.5s = 1 second to detect
// Webcam captures at 640x480 for accurate face/object detection
const VIDEO_W = 640;
const VIDEO_H = 480;
// Snapshots stored at lower resolution/quality for storage optimization
const STORE_W = 320;
const STORE_H = 240;
const SNAPSHOT_QUALITY = 0.30; // ~5-7KB per stored frame

// Thresholds — only flag very deliberate head turns (avoid false positives)
const NOSE_TURN_THRESHOLD = 0.32;     // 32% offset = very clearly turned away

// ── CDN sources to try (in order) ────────────────────

const SCRIPT_URLS = [
  "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js",
  "https://unpkg.com/face-api.js@0.22.2/dist/face-api.min.js",
  "https://cdnjs.cloudflare.com/ajax/libs/face-api.js/0.22.2/face-api.min.js",
];

const MODEL_URLS = [
  "https://justadudewhohacks.github.io/face-api.js/models",
  "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights",
  "https://unpkg.com/face-api.js@0.22.2/weights",
];

// ── COCO-SSD for object (phone) detection ────────────

const COCO_SCRIPTS = [
  "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.10.0/dist/tf.min.js",
  "https://unpkg.com/@tensorflow/tfjs@4.10.0/dist/tf.min.js",
];
const COCO_MODEL_SCRIPTS = [
  "https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js",
  "https://unpkg.com/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js",
];

let cocoModel = null;
let cocoLoading = false;

async function loadCocoSsd() {
  if (cocoModel) return cocoModel;
  if (cocoLoading) {
    for (let i = 0; i < 150; i++) {
      await new Promise((r) => setTimeout(r, 200));
      if (cocoModel) return cocoModel;
    }
    return null;
  }
  cocoLoading = true;
  try {
    // Load TensorFlow.js
    if (!window.tf) {
      for (const url of COCO_SCRIPTS) {
        try {
          await loadScript(url, 15000);
          if (window.tf) { console.log("[Proctor] tf.js loaded"); break; }
        } catch { /* next */ }
      }
    }
    if (!window.tf) throw new Error("TensorFlow.js failed to load");

    // Load COCO-SSD
    if (!window.cocoSsd) {
      for (const url of COCO_MODEL_SCRIPTS) {
        try {
          await loadScript(url, 15000);
          if (window.cocoSsd) { console.log("[Proctor] coco-ssd loaded"); break; }
        } catch { /* next */ }
      }
    }
    if (!window.cocoSsd) throw new Error("COCO-SSD script failed to load");

    // Load model
    console.log("[Proctor] Loading COCO-SSD model...");
    cocoModel = await window.cocoSsd.load({ base: "mobilenet_v2" });
    console.log("[Proctor] COCO-SSD model ready (mobilenet_v2) — phone detection ACTIVE");
    return cocoModel;
  } catch (e) {
    console.warn("[Proctor] COCO-SSD load failed:", e.message);
    cocoLoading = false;
    return null;
  }
}

// Prohibited objects — flag phones and similar handheld devices
const BANNED_OBJECTS = ["cell phone", "remote"];
const PHONE_CONFIDENCE = 0.25; // 25% confidence — catch phones even partially visible

let faceApiLoaded = false;
let faceApiLoading = false;

async function loadScript(url, timeout = 10000) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    const timer = setTimeout(() => {
      reject(new Error(`Script timeout: ${url}`));
    }, timeout);
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`Script failed: ${url}`));
    };
    document.head.appendChild(script);
  });
}

async function testModelUrl(url) {
  // Quick check: can we fetch the manifest JSON?
  try {
    const resp = await fetch(
      `${url}/tiny_face_detector_model-weights_manifest.json`,
      { method: "HEAD", mode: "cors", cache: "no-store" }
    );
    return resp.ok;
  } catch {
    return false;
  }
}

async function loadFaceApi() {
  if (faceApiLoaded) return true;
  if (faceApiLoading) {
    for (let i = 0; i < 150; i++) {
      await new Promise((r) => setTimeout(r, 200));
      if (faceApiLoaded) return true;
    }
    return false;
  }
  faceApiLoading = true;

  try {
    // Step 1: Load the face-api.js library
    if (!window.faceapi) {
      let scriptLoaded = false;
      for (const url of SCRIPT_URLS) {
        try {
          console.log(`[Proctor] Trying script: ${url}`);
          await loadScript(url);
          if (window.faceapi) {
            console.log(`[Proctor] Script loaded from: ${url}`);
            scriptLoaded = true;
            break;
          }
        } catch (e) {
          console.warn(`[Proctor] Script failed: ${url}`, e.message);
        }
      }
      if (!scriptLoaded || !window.faceapi) {
        throw new Error("All script CDNs failed");
      }
    }

    // Step 2: Find a working model URL
    let modelUrl = null;
    for (const url of MODEL_URLS) {
      console.log(`[Proctor] Testing model URL: ${url}`);
      const ok = await testModelUrl(url);
      if (ok) {
        modelUrl = url;
        console.log(`[Proctor] Model URL reachable: ${url}`);
        break;
      } else {
        console.warn(`[Proctor] Model URL unreachable: ${url}`);
      }
    }

    if (!modelUrl) {
      throw new Error("All model CDNs unreachable");
    }

    // Step 3: Load both models
    console.log(`[Proctor] Loading models from: ${modelUrl}`);
    await Promise.all([
      window.faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
      window.faceapi.nets.faceLandmark68TinyNet.loadFromUri(modelUrl),
    ]);

    faceApiLoaded = true;
    console.log("[Proctor] face-api.js fully loaded with landmarks");
    return true;
  } catch (e) {
    console.error("[Proctor] face-api.js load FAILED:", e.message);
    faceApiLoading = false;
    return false;
  }
}

export default function Proctor({ sessionId, onViolation }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const snapshotTimerRef = useRef(null);
  const faceTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const faceApiReady = useRef(false);
  const cocoReady = useRef(false);
  const objectTimerRef = useRef(null);
  const lastViolationTime = useRef({});
  const consecutiveNoFace = useRef(0);

  // Snapshot queue — avoid overlapping uploads
  const snapshotUploading = useRef(false);

  const canReport = useCallback((eventType) => {
    const now = Date.now();
    const last = lastViolationTime.current[eventType] || 0;
    const cooldown = eventType === "no_face" ? NO_FACE_COOLDOWN : VIOLATION_COOLDOWN;
    if (now - last < cooldown) return false;
    lastViolationTime.current[eventType] = now;
    return true;
  }, []);

  // Capture at storage quality (320x240, low JPEG) for uploading snapshots
  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return null;
    canvas.width = STORE_W;
    canvas.height = STORE_H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, STORE_W, STORE_H);
    return canvas.toDataURL("image/jpeg", SNAPSHOT_QUALITY);
  }, []);

  const sendViolation = useCallback(
    async (eventType, description, withSnapshot = true) => {
      if (!mountedRef.current) return;
      if (!canReport(eventType)) return;
      try {
        const snap = withSnapshot ? captureFrame() : null;
        console.warn(`[Proctor] VIOLATION: ${eventType} — ${description}`);
        await reportViolation({
          session_id: sessionId,
          event_type: eventType,
          description,
          snapshot_base64: snap,
        });
        onViolation?.(eventType, description);
        // Also send an extra snapshot on violation for evidence
        if (withSnapshot && !snapshotUploading.current) {
          const extraSnap = captureFrame();
          if (extraSnap) {
            snapshotUploading.current = true;
            uploadSnapshot({ session_id: sessionId, event_type: "violation_snapshot", description: `Snapshot at violation: ${eventType}`, snapshot_base64: extraSnap }).catch(() => {}).finally(() => { snapshotUploading.current = false; });
          }
        }
      } catch (e) {
        console.warn("[Proctor] violation report failed:", e);
      }
    },
    [sessionId, captureFrame, onViolation, canReport]
  );

  // ── Snapshot every 2s (non-blocking queue) ─────────

  const sendSnapshot = useCallback(async () => {
    if (!mountedRef.current || snapshotUploading.current) return;
    const snap = captureFrame();
    if (!snap) return;
    snapshotUploading.current = true;
    try {
      await uploadSnapshot({
        session_id: sessionId,
        event_type: "snapshot",
        description: "Periodic webcam snapshot",
        snapshot_base64: snap,
      });
    } catch (e) {
      // Silently skip failed uploads to not block the queue
    } finally {
      snapshotUploading.current = false;
    }
  }, [sessionId, captureFrame]);

  // ── Strict face analysis ───────────────────────────

  const checkFaces = useCallback(async () => {
    if (!faceApiReady.current || !videoRef.current || videoRef.current.readyState < 2) return;

    try {
      const detections = await window.faceapi
        .detectAllFaces(
          videoRef.current,
          new window.faceapi.TinyFaceDetectorOptions({
            inputSize: 320,
            scoreThreshold: 0.2,
          })
        )
        .withFaceLandmarks(true);

      // ── CHECK 1: No face — person not visible ──
      if (detections.length === 0) {
        consecutiveNoFace.current++;
        if (consecutiveNoFace.current >= CONSECUTIVE_NO_FACE_THRESHOLD) {
          sendViolation("no_face", "Person not visible in frame");
        }
        return;
      }

      consecutiveNoFace.current = 0;

      // Find largest face (the test-taker) — ignore small background faces
      const largest = detections.reduce((a, b) =>
        (b.detection.box.width * b.detection.box.height) >
        (a.detection.box.width * a.detection.box.height) ? b : a
      );
      const landmarks = largest.landmarks;

      // ── CHECK 2: Head turn via nose-jaw landmarks ──
      if (landmarks) {
        const nose = landmarks.getNose();
        const jawline = landmarks.getJawOutline();

        if (nose.length > 0 && jawline.length > 0) {
          const noseTip = nose[3] || nose[Math.floor(nose.length / 2)];
          const jawLeft = jawline[0];
          const jawRight = jawline[jawline.length - 1];
          const jawWidth = Math.abs(jawRight.x - jawLeft.x);
          const jawCenterX = (jawLeft.x + jawRight.x) / 2;

          if (jawWidth > 0) {
            const noseOffset = (noseTip.x - jawCenterX) / jawWidth;
            if (Math.abs(noseOffset) > NOSE_TURN_THRESHOLD) {
              const dir = noseOffset > 0 ? "right" : "left";
              sendViolation("head_turn", `Person looking ${dir} — not facing screen`);
            }
          }
        }
      }
    } catch (e) {
      console.warn("[Proctor] face detection error:", e);
    }
  }, [sendViolation]);

  // ── Object detection (phone, laptop, book) ─────────

  const checkObjects = useCallback(async () => {
    if (!cocoReady.current || !videoRef.current || videoRef.current.readyState < 2) return;
    try {
      const predictions = await cocoModel.detect(videoRef.current);
      for (const pred of predictions) {
        if (BANNED_OBJECTS.includes(pred.class) && pred.score >= PHONE_CONFIDENCE) {
          const label = pred.class === "cell phone" ? "Mobile phone" : pred.class === "remote" ? "Handheld device" : pred.class;
          sendViolation(
            "phone_detected",
            `${label} detected in frame (${(pred.score * 100).toFixed(0)}% confidence) — prohibited device usage`
          );
          return; // one violation per check cycle
        }
      }
    } catch (e) {
      console.warn("[Proctor] Object detection error:", e);
    }
  }, [sendViolation]);

  // ── Tab visibility ─────────────────────────────────

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        sendViolation("tab_switch", "User switched away from quiz tab", false);
      }
    };
    const handleBlur = () => {
      sendViolation("tab_switch", "Quiz window lost focus", false);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
    };
  }, [sendViolation]);

  // ── Split screen / window resize detection ────────
  useEffect(() => {
    let lastSize = { w: window.outerWidth, h: window.outerHeight };

    const checkWindowSize = () => {
      const screenW = window.screen.availWidth;
      const screenH = window.screen.availHeight;
      const winW = window.outerWidth;
      const winH = window.outerHeight;

      // If window takes less than 80% of screen, likely split-screened or resized
      const widthRatio = winW / screenW;
      const heightRatio = winH / screenH;

      if (widthRatio < 0.80 || heightRatio < 0.75) {
        sendViolation(
          "window_resize",
          `Window appears resized or split-screen (${Math.round(widthRatio * 100)}% x ${Math.round(heightRatio * 100)}% of screen)`,
          false
        );
      }
    };

    // Check on resize events
    const handleResize = () => {
      const newW = window.outerWidth;
      const newH = window.outerHeight;
      // Only check if significant change (avoid false positives from toolbar, etc.)
      if (Math.abs(newW - lastSize.w) > 100 || Math.abs(newH - lastSize.h) > 100) {
        lastSize = { w: newW, h: newH };
        checkWindowSize();
      }
    };

    // Initial check after a short delay (let browser settle)
    const initTimer = setTimeout(checkWindowSize, 3000);
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [sendViolation]);

  // ── Multiple monitor detection ────────────────────
  useEffect(() => {
    // Check if window is positioned outside primary screen bounds (second monitor)
    const checkMonitor = () => {
      const screenW = window.screen.availWidth;
      const winX = window.screenX || window.screenLeft || 0;
      // If window starts beyond the primary screen width, likely on external display
      if (winX < -50 || winX > screenW + 50) {
        sendViolation("external_display", "Quiz window detected on external/secondary display", false);
      }
    };

    const timer = setInterval(checkMonitor, 15000); // check every 15s
    setTimeout(checkMonitor, 5000); // initial check
    return () => clearInterval(timer);
  }, [sendViolation]);

  // ── Main init ──────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    async function init() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: VIDEO_W },
            height: { ideal: VIDEO_H },
            facingMode: "user",
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => {});
          await new Promise((resolve) => {
            if (video.readyState >= 2) return resolve();
            video.addEventListener("loadeddata", resolve, { once: true });
          });
          console.log(`[Proctor] Webcam active: ${video.videoWidth}x${video.videoHeight}`);
        }

        // Start snapshots every 2s immediately (this always works)
        sendSnapshot();
        snapshotTimerRef.current = setInterval(sendSnapshot, SNAPSHOT_INTERVAL);
        console.log("[Proctor] Snapshots active — every 2s");

        // Try loading face detection (bonus layer on top of snapshots)
        // Load face detection + object detection in parallel
        console.log("[Proctor] Loading detection models...");
        const [faceLoaded, cocoModelResult] = await Promise.all([
          loadFaceApi(),
          loadCocoSsd(),
        ]);

        if (faceLoaded && !cancelled) {
          faceApiReady.current = true;
          console.log("[Proctor] Face detection ACTIVE");
          checkFaces();
          faceTimerRef.current = setInterval(checkFaces, FACE_CHECK_INTERVAL);
        } else {
          console.warn("[Proctor] Face detection unavailable — snapshots still active");
        }

        if (cocoModelResult && !cancelled) {
          cocoReady.current = true;
          console.log("[Proctor] Phone/object detection ACTIVE");
          checkObjects();
          objectTimerRef.current = setInterval(checkObjects, 3000); // check every 3s
        } else {
          console.warn("[Proctor] Object detection unavailable");
        }
      } catch (err) {
        console.error("[Proctor] Media access error:", err);
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          sendViolation("webcam_denied", "User denied webcam access — proctoring disabled", false);
        } else {
          sendViolation("webcam_denied", `Webcam error: ${err.message}`, false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current);
      if (faceTimerRef.current) clearInterval(faceTimerRef.current);
      if (objectTimerRef.current) clearInterval(objectTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [sessionId, checkFaces, checkObjects, sendSnapshot, sendViolation]);

  return (
    <>
      <video
        ref={videoRef}
        width={VIDEO_W}
        height={VIDEO_H}
        style={{
          position: "fixed",
          top: -9999,
          left: -9999,
          width: VIDEO_W,
          height: VIDEO_H,
          opacity: 0,
          pointerEvents: "none",
        }}
        muted
        playsInline
      />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </>
  );
}
